import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import ConfirmModal from "../../components/admin/ConfirmModal";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useAdminToast } from "../../context/AdminToastContext";
import { adminService } from "../../features/admin/service";
import type { OrgCandidateUser, OrganizationSummary } from "../../features/admin/types";
import { formatISTDateTime } from "../../utils/dateUtils";

/** Minimal identity used everywhere a raw user_id would otherwise be shown. */
interface DirectoryUser {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  /** Set when this user already belongs to an org (any org) — a user belongs
   *  to at most one at a time, so they can't be added/promoted elsewhere
   *  until removed from this one. */
  orgId?: string;
  orgName?: string;
  orgStatus?: string;
}

function extractPhone(user: any): string | undefined {
  return user?.phone_number || user?.phone || user?.whatsapp_number || user?.whatsapp || undefined;
}

/** Search-by-name/email/phone picker, backed by the same /api/admin/users
 *  search the Users page uses — reused for both "promote to admin" and
 *  "add an outside candidate" so superadmin never has to type a raw user_id. */
const UserPicker: React.FC<{
  placeholder: string;
  actionLabel: string;
  busy: boolean;
  onPick: (user: DirectoryUser) => void;
  /** "promote" scopes results to ACTIVE members of `org` only — promoting only
   *  ever makes sense for someone already in this exact org. "candidate" scopes
   *  to users with no org at all — anyone already attached (here or elsewhere)
   *  can't be added again. */
  mode: "promote" | "candidate";
  org: OrganizationSummary;
}> = ({ placeholder, actionLabel, busy, onPick, mode, org }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Portal the dropdown to <body> — the search box lives inside a table's
  // overflow-x-auto wrapper, which clips any absolutely-positioned child no
  // matter its z-index. Reposition on scroll/resize while it's open.
  useEffect(() => {
    const open = query.trim().length >= 2;
    if (!open) {
      setMenuRect(null);
      return;
    }
    const update = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setMenuRect({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [query, results]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        // Wide net server-side (name/email/phone across all users) — narrowed
        // to this picker's actual eligibility rule client-side below, since
        // /api/admin/users has no "members of org X" filter.
        const res = await adminService.getUsers({ search: q, page_size: 25 });
        const mapped: DirectoryUser[] = (res.items || []).map((u: any) => ({
          id: u.id || u._id,
          name: u.name,
          email: u.email,
          phone: extractPhone(u),
          orgId: u.organization?.org_id,
          orgName: u.organization?.org_name,
          orgStatus: u.organization?.status,
        }));
        const scoped =
          mode === "promote"
            ? mapped.filter((u) => u.orgId === org.org_id && u.orgStatus === "active")
            : mapped.filter((u) => !u.orgId);
        setResults(scoped.slice(0, 6));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, mode, org.org_id]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-md text-xs text-white focus:outline-none focus:border-indigo-500 min-w-[260px]"
      />
      {menuRect && createPortal(
        <div
          style={{ position: "fixed", top: menuRect.top + 4, left: menuRect.left, width: Math.max(menuRect.width, 280), maxHeight: "16rem" }}
          className="z-[100] bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-y-auto"
        >
          {searching ? (
            <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">
              {mode === "promote"
                ? `No active members of "${org.name}" match by name, email, or phone.`
                : "No unattached users match by name, email, or phone."}
            </div>
          ) : (
            results.map((u) => {
              return (
                <button
                  key={u.id}
                  type="button"
                  disabled={busy}
                  onClick={() => { onPick(u); setQuery(""); setResults([]); }}
                  className="w-full text-left px-3 py-2 hover:enabled:bg-gray-800 transition-colors border-b border-gray-800 last:border-0 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="text-xs text-white font-medium truncate">{u.name || "(no name)"}</div>
                  <div className="text-[11px] text-gray-500 truncate">{[u.email, u.phone].filter(Boolean).join(" · ") || u.id}</div>
                  <div className="text-[11px] text-indigo-400">{busy ? "Working…" : actionLabel}</div>
                </button>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

interface EditOrgModalProps {
  org: OrganizationSummary;
  onClose: () => void;
  onSaved: () => void;
}

/** seat_limit stays superadmin-only by design (no self-serve billing). It is a
 *  plain integer cap on active members — there is no tier system. */
const EditOrgModal: React.FC<EditOrgModalProps> = ({ org, onClose, onSaved }) => {
  useBodyScrollLock();
  const [seatLimit, setSeatLimit] = useState(String(org.seat_limit ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    if (!seatLimit || Number(seatLimit) < 1) {
      setError("Seat limit must be at least 1.");
      return;
    }
    setSaving(true);
    try {
      await adminService.patchOrganization(org.org_id, { seat_limit: Number(seatLimit) });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update organization.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white break-words">{org.name}</h2>
            <p className="text-sm text-gray-400 mt-0.5">Seat limit</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-sm shrink-0">Close</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Seat limit</label>
            <input
              type="number"
              min={1}
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-indigo-500"
            />
            {org.active_member_count > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {org.active_member_count} seat{org.active_member_count === 1 ? " is" : "s are"} already in use.
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 rounded-md p-3 mt-4 break-words">{error}</div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Read-only detail view: every org identity field, plus which admin's
 *  personal data those fields fell back to at creation ("first-in" admin —
 *  org_admin_user_ids[0] — see create_organization's fallback logic).
 *  Superadmin-supplied values may have overwritten that fallback since, so
 *  this is "where it started", not a live provenance guarantee. */
const ViewOrgModal: React.FC<{
  org: OrganizationSummary;
  firstAdmin?: DirectoryUser;
  onClose: () => void;
}> = ({ org, firstAdmin, onClose }) => {
  useBodyScrollLock();
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: "Agency / company name", value: org.canonical_agency_name },
    { label: "Website", value: org.website },
    { label: "GST number(s)", value: org.gst_numbers?.length ? org.gst_numbers.join(", ") : null },
    {
      label: "Business registration ID(s)",
      value: org.business_registration_ids?.length ? org.business_registration_ids.join(", ") : null,
    },
    { label: "PAN / TAN(s)", value: org.pan_tan_numbers?.length ? org.pan_tan_numbers.join(", ") : null },
    {
      label: "Office address",
      value: org.offices?.length
        ? [org.offices[0].address, org.offices[0].city, org.offices[0].state, org.offices[0].country]
            .filter(Boolean)
            .join(", ")
        : null,
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white break-words">{org.name}</h2>
            <p className="text-sm text-gray-400 mt-0.5">{org.slug} · {org.status}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-sm shrink-0">Close</button>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-800/40 px-3 py-2.5 mb-4 text-xs text-gray-400">
          Org-based data used in flyers, itineraries, videos, and GST/website verification gating for every
          active member — in place of that member's own value.
        </div>

        <div className="divide-y divide-gray-800 rounded-lg border border-gray-800 mb-4">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <span className="text-xs text-gray-500">{row.label}</span>
              <span className="text-sm text-white text-right">{row.value || "Not set"}</span>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-gray-800 px-3 py-2.5">
          <p className="text-xs text-gray-500 mb-1">Copied from ("first-in" admin)</p>
          {org.org_admin_user_ids?.length ? (
            firstAdmin ? (
              <p className="text-sm text-white">
                {firstAdmin.name || firstAdmin.id}
                {firstAdmin.email && <span className="text-gray-500"> · {firstAdmin.email}</span>}
                {firstAdmin.phone && <span className="text-gray-500"> · {firstAdmin.phone}</span>}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Resolving…</p>
            )
          ) : (
            <p className="text-sm text-gray-500">No admin on record.</p>
          )}
          <p className="text-xs text-gray-600 mt-1">
            Any field a superadmin later edited directly may no longer match this admin's own data.
          </p>
        </div>
      </div>
    </div>
  );
};

type LifecycleAction = { type: "suspend" | "reactivate"; org: OrganizationSummary };

const AdminOrganizationsPage: React.FC = () => {
  const [orgs, setOrgs] = useState<OrganizationSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editOrg, setEditOrg] = useState<OrganizationSummary | null>(null);
  const [lifecycleAction, setLifecycleAction] = useState<LifecycleAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [linkBusyUserId, setLinkBusyUserId] = useState<string | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<{ org: OrganizationSummary; userId: string } | null>(null);
  const [roleBusyUserId, setRoleBusyUserId] = useState<string | null>(null);
  const [directory, setDirectory] = useState<Record<string, DirectoryUser>>({});
  const [candidateBusyId, setCandidateBusyId] = useState<string | null>(null);
  const [poolCandidates, setPoolCandidates] = useState<OrgCandidateUser[] | null>(null);
  const [poolMembers, setPoolMembers] = useState<OrgCandidateUser[] | null>(null);
  const [poolLoading, setPoolLoading] = useState(false);
  const [viewOrgId, setViewOrgId] = useState<string | null>(null);
  const { alertModal, showAlert } = useAlertModal();
  const { showSuccess, showError } = useAdminToast();

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminService.getOrganizations(statusFilter === "all" ? undefined : statusFilter);
      setOrgs(items);
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to load organizations.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  /** Real candidate/active-member lists for the currently expanded org — the
   *  directory row only shows counts, this is what actually backs them. */
  const fetchPool = useCallback(async (orgId: string) => {
    setPoolLoading(true);
    try {
      const [candidates, members] = await Promise.all([
        adminService.getOrgCandidatesList(orgId),
        adminService.getOrgMembersList(orgId),
      ]);
      setPoolCandidates(candidates);
      setPoolMembers(members);
    } catch (err: any) {
      console.error(err);
      setPoolCandidates([]);
      setPoolMembers([]);
    } finally {
      setPoolLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!expandedOrgId) {
      setPoolCandidates(null);
      setPoolMembers(null);
      return;
    }
    fetchPool(expandedOrgId);
  }, [expandedOrgId, fetchPool]);

  /** Resolves raw admin user_ids into name/email/phone the first time an org's
   *  admin list is expanded, so superadmin never has to read a bare ObjectId. */
  useEffect(() => {
    if (!orgs) return;
    // Resolve admins for whichever org row is expanded AND whichever org is
    // open in the "View" detail modal (org_admin_user_ids[0] there is the
    // "first-in" admin whose personal data the identity fields fell back to).
    const relevantOrgIds = [expandedOrgId, viewOrgId].filter(Boolean) as string[];
    if (relevantOrgIds.length === 0) return;
    const ids = Array.from(
      new Set(
        relevantOrgIds.flatMap((oid) => orgs.find((o) => o.org_id === oid)?.org_admin_user_ids ?? []),
      ),
    ).filter((id) => !directory[id]);
    if (ids.length === 0) return;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const u = await adminService.getUser(id);
            return [id, { id, name: u?.name, email: u?.email, phone: extractPhone(u) }] as const;
          } catch {
            return [id, { id }] as const;
          }
        }),
      );
      setDirectory((prev) => {
        const next = { ...prev };
        for (const [id, u] of entries) next[id] = u;
        return next;
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedOrgId, viewOrgId, orgs]);

  /** Mints a one-off org-admin login link and puts it on the clipboard. The URL
   *  is never rendered — it is a credential, so it only ever lives in the
   *  clipboard on its way into a message to that admin. */
  const handleCopyMagicLink = async (org: OrganizationSummary, userId: string) => {
    setLinkBusyUserId(userId);
    try {
      const { magic_link } = await adminService.createOrgAdminMagicLink(org.org_id, userId);
      await navigator.clipboard.writeText(magic_link);
      showSuccess(`Link copied — send it to ${userId} to log them into the org-admin console.`);
    } catch (err: any) {
      console.error(err);
      showError(err?.response?.data?.detail || "Failed to create the org-admin link.");
    } finally {
      setLinkBusyUserId(null);
    }
  };

  const handlePromote = async (org: OrganizationSummary, user: DirectoryUser) => {
    setRoleBusyUserId(user.id);
    try {
      await adminService.promoteOrgAdmin(org.org_id, user.id);
      setDirectory((prev) => ({ ...prev, [user.id]: user }));
      showSuccess(`${user.name || user.id} is now an Org Admin.`);
      fetchOrgs();
      fetchPool(org.org_id);
    } catch (err: any) {
      console.error(err);
      showError(err?.response?.data?.detail || "Failed to promote this user — make sure they're an active member of this org.");
    } finally {
      setRoleBusyUserId(null);
    }
  };

  /** Adds any existing user to this org's candidate pool — not restricted to
   *  users who matched the original clustering search. The Org Admin can then
   *  invite them from that pool like any other candidate. */
  const handleAddCandidate = async (org: OrganizationSummary, user: DirectoryUser) => {
    setCandidateBusyId(user.id);
    try {
      await adminService.addOrgCandidates(org.org_id, [user.id]);
      setDirectory((prev) => ({ ...prev, [user.id]: user }));
      showSuccess(`${user.name || user.id} added to "${org.name}"'s candidate pool.`);
      fetchOrgs();
      fetchPool(org.org_id);
    } catch (err: any) {
      console.error(err);
      showError(err?.response?.data?.detail || "Failed to add this user to the candidate pool.");
    } finally {
      setCandidateBusyId(null);
    }
  };

  const handleRemoveCandidate = async (org: OrganizationSummary, user: OrgCandidateUser) => {
    setCandidateBusyId(user.user_id);
    try {
      await adminService.removeOrgCandidate(org.org_id, user.user_id);
      showSuccess(`${user.name || user.email || user.user_id} removed from the candidate pool.`);
      fetchOrgs();
      fetchPool(org.org_id);
    } catch (err: any) {
      console.error(err);
      showError(err?.response?.data?.detail || "Failed to remove this candidate.");
    } finally {
      setCandidateBusyId(null);
    }
  };

  const handleDemote = async () => {
    if (!demoteTarget) return;
    const { org, userId } = demoteTarget;
    setRoleBusyUserId(userId);
    try {
      await adminService.demoteOrgAdmin(org.org_id, userId);
      setDemoteTarget(null);
      showSuccess(`${directory[userId]?.name || userId} is no longer an Org Admin.`);
      fetchOrgs();
      fetchPool(org.org_id);
    } catch (err: any) {
      console.error(err);
      showError(err?.response?.data?.detail || "Failed to demote this admin.");
    } finally {
      setRoleBusyUserId(null);
    }
  };

  const handleLifecycle = async (action: LifecycleAction) => {
    setActionBusy(true);
    try {
      if (action.type === "suspend") {
        await adminService.suspendOrganization(action.org.org_id);
      } else {
        await adminService.reactivateOrganization(action.org.org_id);
      }
      setLifecycleAction(null);
      fetchOrgs();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to update organization status.");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <>
      <div>
        <AdminPageHeader
          title="Organizations"
          subtitle="Every organization superadmin has created from a user cluster — admins, seat usage and lifecycle."
          meta={orgs ? `${orgs.length} org${orgs.length === 1 ? "" : "s"}` : undefined}
          actions={
            <div className="flex items-center gap-2">
              <Link
                to="/admin/organizations/clustering"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors shrink-0"
              >
                New from cluster
              </Link>
              <RefreshButton onRefresh={fetchOrgs} />
            </div>
          }
        />

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value
                  ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30"
                  : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Organization</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Admins</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Seats</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Invited</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Candidates</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Created</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
                ) : !orgs || orgs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center text-gray-500">
                      {statusFilter === "all" ? (
                        <>
                          No organizations yet.{" "}
                          <Link to="/admin/organizations/clustering" className="text-indigo-400 hover:text-indigo-300">
                            Find a cluster to create one from
                          </Link>
                          .
                        </>
                      ) : (
                        `No ${statusFilter} organizations.`
                      )}
                    </td>
                  </tr>
                ) : (
                  orgs.map((org) => {
                    const seatsFull = org.seat_limit > 0 && org.active_member_count >= org.seat_limit;
                    const admins = org.org_admin_user_ids ?? [];
                    const expanded = expandedOrgId === org.org_id;
                    return (
                      <Fragment key={org.org_id}>
                      <tr className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                        <td className="py-3 px-4">
                          <div className="text-white font-medium">{org.name}</div>
                          <div className="text-xs text-gray-600">{org.slug}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                            org.status === "active" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                          }`}>
                            {org.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setExpandedOrgId(expanded ? null : org.org_id)}
                            className="text-indigo-400 hover:text-indigo-300 text-xs"
                            aria-expanded={expanded}
                          >
                            {admins.length === 0 ? "No admins" : `${admins.length} admin${admins.length === 1 ? "" : "s"}`} {expanded ? "▾" : "▸"}
                          </button>
                        </td>
                        <td className={`py-3 px-4 ${seatsFull ? "text-amber-400" : "text-gray-300"}`}>
                          {org.active_member_count}/{org.seat_limit}
                        </td>
                        <td className="py-3 px-4 text-gray-400">{org.invited_count}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setExpandedOrgId(expanded ? null : org.org_id)}
                            className="text-indigo-400 hover:text-indigo-300 text-xs"
                            aria-expanded={expanded}
                          >
                            {org.candidate_count} {expanded ? "▾" : "▸"}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatISTDateTime(org.created_at)}</td>
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <button
                            onClick={() => setViewOrgId(org.org_id)}
                            className="px-2 py-1 text-xs text-gray-300 hover:bg-gray-700/40 rounded-md transition-colors mr-1"
                          >
                            View
                          </button>
                          <button
                            onClick={() => setEditOrg(org)}
                            className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors mr-1"
                          >
                            Edit seat limit
                          </button>
                          {org.status === "active" ? (
                            <button
                              onClick={() => setLifecycleAction({ type: "suspend", org })}
                              className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors"
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => setLifecycleAction({ type: "reactivate", org })}
                              className="px-2 py-1 text-xs text-green-400 hover:bg-green-500/10 rounded-md transition-colors"
                            >
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-gray-800/50 bg-gray-800/20">
                          <td colSpan={8} className="py-4 px-4 space-y-5">
                            <div>
                            <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Org Admins</h4>
                            <p className="text-xs text-gray-500 mb-2">
                              Copy a one-off login link, or demote back to a plain member.
                            </p>
                            {admins.length === 0 ? (
                              <p className="text-xs text-gray-600 mb-3">No Org Admins yet.</p>
                            ) : (
                              <ul className="space-y-1.5 mb-3">
                                {admins.map((userId) => {
                                  const u = directory[userId];
                                  return (
                                    <li key={userId} className="flex flex-wrap items-center gap-3">
                                      <div className="min-w-[160px]">
                                        <div className="text-xs text-gray-200">{u?.name || (u ? "(no name)" : "Loading…")}</div>
                                        <div className="text-[11px] text-gray-500">{[u?.email, u?.phone].filter(Boolean).join(" · ") || userId}</div>
                                      </div>
                                      <button
                                        onClick={() => handleCopyMagicLink(org, userId)}
                                        disabled={linkBusyUserId === userId}
                                        className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors disabled:opacity-50"
                                      >
                                        {linkBusyUserId === userId ? "Copying…" : "Copy org-admin link"}
                                      </button>
                                      <button
                                        onClick={() => setDemoteTarget({ org, userId })}
                                        disabled={roleBusyUserId === userId}
                                        className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors disabled:opacity-50"
                                      >
                                        Demote
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            {(() => {
                              const plainMembers = (poolMembers ?? []).filter((m) => m.organization?.role !== "org_admin");
                              return (
                                <>
                                  <h5 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-1">
                                    Active members {poolLoading ? "" : `(${plainMembers.length})`}
                                  </h5>
                                  {poolLoading ? (
                                    <p className="text-xs text-gray-600 mb-2">Loading…</p>
                                  ) : plainMembers.length === 0 ? (
                                    <p className="text-xs text-gray-600 mb-2">No active plain members — every active member is already an admin, or there are none yet.</p>
                                  ) : (
                                    <ul className="space-y-1.5 mb-2">
                                      {plainMembers.map((m) => (
                                        <li key={m.user_id} className="flex flex-wrap items-center gap-3">
                                          <div className="min-w-[160px]">
                                            <div className="text-xs text-gray-200">{m.name || "(no name)"}</div>
                                            <div className="text-[11px] text-gray-500">{[m.email].filter(Boolean).join(" · ") || m.user_id}</div>
                                          </div>
                                          <button
                                            onClick={() => handlePromote(org, { id: m.user_id, name: m.name || undefined, email: m.email || undefined })}
                                            disabled={roleBusyUserId === m.user_id}
                                            className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors disabled:opacity-50"
                                          >
                                            {roleBusyUserId === m.user_id ? "Promoting…" : "Promote to Org Admin"}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              );
                            })()}
                            <p className="text-xs text-gray-500 mb-1">Or search by name, email, or phone:</p>
                            <UserPicker
                              mode="promote"
                              org={org}
                              placeholder="Search active members…"
                              actionLabel="Promote to Org Admin"
                              busy={!!roleBusyUserId}
                              onPick={(user) => handlePromote(org, user)}
                            />
                            </div>

                            <div>
                              <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Candidate pool</h4>
                              <p className="text-xs text-gray-500 mb-1">
                                The pool an Org Admin can invite from. Add any existing user here — not just people from the original cluster match.
                              </p>
                              {poolLoading ? (
                                <p className="text-xs text-gray-600 mb-2">Loading…</p>
                              ) : (poolCandidates ?? []).length === 0 ? (
                                <p className="text-xs text-gray-600 mb-2">No candidates in the pool yet.</p>
                              ) : (
                                <ul className="space-y-1.5 mb-2">
                                  {(poolCandidates ?? []).map((c) => (
                                    <li key={c.user_id} className="flex flex-wrap items-center gap-3">
                                      <div className="min-w-[160px]">
                                        <div className="text-xs text-gray-200">{c.name || "(no name)"}</div>
                                        <div className="text-[11px] text-gray-500">{[c.email].filter(Boolean).join(" · ") || c.user_id}</div>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveCandidate(org, c)}
                                        disabled={candidateBusyId === c.user_id}
                                        className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                                      >
                                        {candidateBusyId === c.user_id ? "Removing…" : "Remove from pool"}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <p className="text-xs text-gray-500 mb-1">Add a new candidate — search by name, email, or phone:</p>
                              <UserPicker
                                mode="candidate"
                                org={org}
                                placeholder="Search all users…"
                                actionLabel="Add to candidate pool"
                                busy={candidateBusyId !== null}
                                onPick={(user) => handleAddCandidate(org, user)}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editOrg && (
        <EditOrgModal
          org={editOrg}
          onClose={() => setEditOrg(null)}
          onSaved={() => { setEditOrg(null); fetchOrgs(); }}
        />
      )}
      {viewOrgId && orgs && (() => {
        const viewOrg = orgs.find((o) => o.org_id === viewOrgId);
        if (!viewOrg) return null;
        const firstAdminId = viewOrg.org_admin_user_ids?.[0];
        return (
          <ViewOrgModal
            org={viewOrg}
            firstAdmin={firstAdminId ? directory[firstAdminId] : undefined}
            onClose={() => setViewOrgId(null)}
          />
        );
      })()}
      {lifecycleAction && (
        <ConfirmModal
          title={lifecycleAction.type === "suspend" ? "Suspend organization" : "Reactivate organization"}
          description={
            lifecycleAction.type === "suspend"
              ? `“${lifecycleAction.org.name}” will be suspended: its Org Admins lose access to the org console immediately, on their very next request.`
              : `“${lifecycleAction.org.name}” becomes active again and its Org Admins regain console access.`
          }
          confirmLabel={lifecycleAction.type === "suspend" ? "Suspend" : "Reactivate"}
          confirmClass={lifecycleAction.type === "suspend" ? "bg-amber-600 hover:bg-amber-500" : "bg-green-600 hover:bg-green-500"}
          irreversible={false}
          busy={actionBusy}
          busyLabel="Working…"
          onConfirm={() => handleLifecycle(lifecycleAction)}
          onCancel={() => setLifecycleAction(null)}
        />
      )}
      {demoteTarget && (
        <ConfirmModal
          title="Demote Org Admin"
          description={`${demoteTarget.userId} loses org-admin console access and becomes a plain member of "${demoteTarget.org.name}". This is refused if they're the org's last remaining admin.`}
          confirmLabel="Demote"
          confirmClass="bg-amber-600 hover:bg-amber-500"
          irreversible={false}
          busy={roleBusyUserId === demoteTarget.userId}
          busyLabel="Demoting…"
          onConfirm={handleDemote}
          onCancel={() => setDemoteTarget(null)}
        />
      )}
      {alertModal}
    </>
  );
};

export default AdminOrganizationsPage;
